import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import QuickActions from '../components/dashboard/QuickActions';
import ActiveJobsList from '../components/dashboard/ActiveJobsList';
import './ServiceProviderDashboard.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ServiceProviderDashboard = () => {
  const { user, token } = useAuth();
  const socket = useSocket();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    availableJobs: 0,
    activeWork: 0,
    completedJobs: 0,
    rating: 0,
    totalRatings: 0
  });
  const [pendingStartContracts, setPendingStartContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const currentDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'short', day: 'numeric'
  });

  const fetchData = useCallback(async () => {
    try {
      const storedToken = token || localStorage.getItem('marilink_token');
      const [rfqsRes, contractsRes, userRes] = await Promise.all([
        fetch(`${API_BASE}/rfqs`, { headers: { Authorization: `Bearer ${storedToken}` } }),
        fetch(`${API_BASE}/contracts`, { headers: { Authorization: `Bearer ${storedToken}` } }),
        fetch(`${API_BASE}/users/me`, { headers: { Authorization: `Bearer ${storedToken}` } })
      ]);

      const rfqsData = await rfqsRes.json();
      const contractsData = await contractsRes.json();
      const userData = await userRes.json();

      const now = new Date();
      const available = (rfqsData.rfqs || []).filter(r => {
        if (r.status !== 'open') return false;
        if (r.deadline && new Date(r.deadline) < now) return false;
        return true;
      }).length;

      let active = 0;
      let completed = 0;
      let readyToStart = [];
      if (contractsData.success) {
        const contracts = contractsData.contracts || [];
        active = contracts.filter(c => c.status === 'work_in_progress').length;
        completed = contracts.filter(c => c.status === 'payment_released').length;
        readyToStart = contracts.filter(c => c.status === 'payment_verified');
      }

      const currentRating = userData.success ? (userData.user?.rating || 0) : (user?.rating || 0);
      const totalRatings = userData.success ? (userData.user?.totalRatings || 0) : (user?.totalRatings || 0);

      setStats({
        availableJobs: available,
        activeWork: active,
        completedJobs: completed,
        rating: currentRating,
        totalRatings: totalRatings
      });
      setPendingStartContracts(readyToStart);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token, user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!socket) return;
    const handleRatingUpdate = (data) => {
      setStats(prev => ({
        ...prev,
        rating: data.newRating,
        totalRatings: data.totalRatings
      }));
    };
    socket.on('rating_updated', handleRatingUpdate);
    return () => socket.off('rating_updated', handleRatingUpdate);
  }, [socket]);

  useEffect(() => {
    if (!socket) return;
    const refresh = () => fetchData();
    const events = [
      'new_rfq', 'bid_accepted', 'work_completed',
      'payment_released', 'contract_status_change',
      'new_bid', 'contract_created'
    ];
    events.forEach(event => socket.on(event, refresh));
    return () => events.forEach(event => socket.off(event, refresh));
  }, [socket, fetchData]);

  const handleStartWork = async (contractId) => {
    if (!window.confirm('Ready to start work on this vessel?')) return;
    try {
      const storedToken = token || localStorage.getItem('marilink_token');
      const res = await fetch(`${API_BASE}/contracts/${contractId}/start-work`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${storedToken}` }
      });
      const data = await res.json();
      if (data.success) {
        alert('Work started successfully!');
        fetchData();
      } else {
        alert(data.message || 'Failed to start work');
      }
    } catch (error) {
      console.error('Start work error:', error);
    }
  };

  if (loading) return (
    <div className="loader-container">
      <i className="fa-solid fa-spinner fa-spin"></i>
    </div>
  );

  const firstName = user?.name?.split(' ')[0] || 'Partner';

  return (
    <div className="service-provider-dashboard">
      <div className="welcome-banner">
        <div className="welcome-text">
          <h1>Hello, {firstName}!</h1>
          <p>Find jobs, submit offers, and track your work</p>
        </div>
        <div className="welcome-date">
          <i className="fa-regular fa-calendar"></i> {currentDate}
        </div>
      </div>

      <div className="od-stats-row">
        <div className="od-stat-box" onClick={() => navigate('/service-provider/available-jobs')}>
          <div className="od-stat-info">
            <span className="od-stat-val">{stats.availableJobs}</span>
            <span className="od-stat-lbl">Available Jobs</span>
          </div>
          <i className="fa-solid fa-briefcase od-stat-icon"></i>
        </div>
        <div className="od-stat-box" onClick={() => navigate('/service-provider/active-work')}>
          <div className="od-stat-info">
            <span className="od-stat-val">{stats.activeWork}</span>
            <span className="od-stat-lbl">Active Work</span>
          </div>
          <i className="fa-solid fa-tasks od-stat-icon"></i>
        </div>
        <div className="od-stat-box" onClick={() => navigate('/service-provider/active-work')}>
          <div className="od-stat-info">
            <span className="od-stat-val">{stats.completedJobs}</span>
            <span className="od-stat-lbl">Completed Jobs</span>
          </div>
          <i className="fa-solid fa-check-circle od-stat-icon"></i>
        </div>
        <div className="od-stat-box" onClick={() => navigate('/service-provider/company-profile')}>
          <div className="od-stat-info">
            <span className="od-stat-val">
              {stats.rating.toFixed(1)}{' '}
              <span style={{ fontSize: '12px', fontWeight: 'normal', color: '#888' }}>
                ({stats.totalRatings} reviews)
              </span>
            </span>
            <span className="od-stat-lbl">Rating</span>
          </div>
          <i className="fa-solid fa-star od-stat-icon"></i>
        </div>
      </div>

      {pendingStartContracts.length > 0 && (
        <div className="od-alerts-wrapper">
          {pendingStartContracts.map(contract => (
            <div key={contract._id} className="od-alert-item approval">
              <div className="od-alert-left">
                <i className="fa-solid fa-play-circle od-alert-icon approval"></i>
                <div className="od-alert-text">
                  <h4>Ready to Start Work</h4>
                  <p>
                    {contract.vesselName} - {contract.serviceType} | Value: ${contract.totalAmount?.toLocaleString()}
                  </p>
                </div>
              </div>
              <button onClick={() => handleStartWork(contract._id)} className="od-btn-action">
                Start Work <i className="fa-solid fa-arrow-right"></i>
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="od-grid">
        <div className="od-col-main">
          <ActiveJobsList userRole="service-provider" />
        </div>
        <div className="od-col-side">
          <QuickActions userRole="service-provider" />
        </div>
      </div>
    </div>
  );
};

export default ServiceProviderDashboard;